import type {
  GroupedItem,
  Item,
  Tag,
  TagGroup,
  ProductListConfig
} from '../models'

type DataType = {
  items: GroupedItem[]
  facets: TagGroup[]
  gcodes: string[]
}

export type FilterStashType = {
  [filterId: TagGroup['tagGroupId']]: Tag['tagId'][]
}

export type EnabledFiltersType = {
  [tagGroupId: string]: string[]
}

export type HistoryFilter = {
  filterId: string
  value: string
}

export const showProduct = (
  variants: Partial<Item>[],
  stash: FilterStashType
) =>
  Object.entries(stash).every(
    ([key, value]) =>
      !value?.length ||
      variants.some((v) =>
        v?.tags?.find((t) => t.tagGroupId === key && value.includes(t.tagId))
      )
  )

const checkIsOptionEnable = (productList: GroupedItem[]) => (option: Tag) =>
  productList.some((p) =>
    p.variants.some((v) => v?.tags?.find((t) => t?.tagId === option.tagId))
  )

export const getEnabledFilters = (
  data: DataType,
  filters: ProductListConfig['filters'],
  filtersStash: FilterStashType
): EnabledFiltersType => {
  const products: GroupedItem[] = data?.items ?? []
  return (
    filters?.filterItems?.reduce((acc, { tagID: tagGroupId }) => {
      const facet = data?.facets?.find((f) => f?.tagGroupId === tagGroupId)
      const filterOptions = facet?.tags ?? []
      const appliedFiltersExcludingCurr = Object.entries(filtersStash).reduce(
        (applied, [key, value]) => ({
          ...applied,
          ...(key !== tagGroupId ? { [key]: value } : {})
        }),
        {}
      )
      const productsExcludingCurrFilter = products.filter((p) =>
        showProduct(p.variants, appliedFiltersExcludingCurr)
      )

      return {
        ...acc,
        ...(facet
          ? {
              [tagGroupId]: filterOptions
                .filter(checkIsOptionEnable(productsExcludingCurrFilter))
                .map((f) => f.tagId)
            }
          : {})
      }
    }, {}) ?? {}
  )
}

export const filterHistoryByStash = (
  filterStash: FilterStashType,
  prevHistoryState: HistoryFilter[]
) =>
  prevHistoryState.filter(({ filterId, value }) =>
    Object.entries(filterStash)?.find(
      ([filterKey, filterValues]) =>
        filterKey === filterId && filterValues.includes(value)
    )
  )
export const getHistoryFromStash = (stash: FilterStashType) =>
  Object.entries(stash).reduce((acc: HistoryFilter[], [key, values]) => {
    const formattedValues = values.map((v) => ({
      filterId: key,
      value: v
    }))
    return [...acc, ...formattedValues]
  }, [])

export const getMatchedVariant = (
  variants: Item[],
  historyFilters: {
    filterId: string
    value: string
  }[]
) => {
  let result = variants?.[0]
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const f = historyFilters
    .slice()
    .reverse()
    .find(({ filterId, value }) => {
      const found = variants?.find((v) =>
        v?.tags?.find((t) => t.tagGroupId === filterId && value === t.tagId)
      )
      if (found) {
        result = found
        return true
      }
      return false
    })
  return result
}
